<template>
  <div class="iframe-container">
    <!-- 加载本地打包后的外部项目 -->
    <iframe
      ref="localIframe"
      :src="iframeSrc"
      width="100%"
      height="100%"
      frameborder="0"
      scrolling="auto"
      @load="handleIframeLoad"
    ></iframe>
  </div>
</template>

<script>
import { getCookie, removeCookie, setCookie } from '@/assets/js/cookie.js'
export default {
  name: 'FlowIframe',
  props: {
    // 外部项目的页面路径（相对路径，基于micro-flow目录）
    pagePath: {
      type: String,
      default: ''
    },
    // 完整的自定义路径（优先级高于pagePath）
    customPath: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // 本地外部项目的基础路径（static目录下的路径）
      // micro-flow是单页应用，只有一个index.html入口，通过前端路由管理不同页面
      basePath: '/static/micro-flow/index.html'
    }
  },
  created() {
    // 在组件创建时提前读取、设置cookie和验证参数
    this.readParams() // 先读取所有参数
  },
  activated(){
    this.readParams() // 先读取所有参数
  },
  computed: {
    // 动态计算iframe的完整路径
    iframeSrc() {
      // 获取所有需要传递的查询参数（除了customPath）
      const queryParams = this.getQueryParams()
      const queryString = this.buildQueryString(queryParams)

      // 优先从路由查询参数获取customPath
      const queryCustomPath = this.$route.query.customPath
      if (queryCustomPath) {
        console.info('从路由查询参数获取customPath:', queryCustomPath)
        return this.addQueryParamsToHashUrl(queryCustomPath, queryString)
      }

      // 如果提供了完整的自定义路径，优先使用
      if (this.customPath) {
        console.info('从props获取customPath:', this.customPath)
        return this.addQueryParamsToHashUrl(this.customPath, queryString)
      }

      // 如果提供了页面路径，构建完整路径
      if (this.pagePath) {
        console.info('从props获取pagePath:', this.pagePath)
        let baseUrl
        // 检查basePath是否已经包含#号
        if (this.basePath.includes('#')) {
          baseUrl = `${this.basePath}${this.pagePath.startsWith('/') ? this.pagePath : `/${this.pagePath}`}`
        } else {
          // 如果是hash模式，添加#号和flow前缀
          // micro-flow项目的路由需要包含flow前缀
          const flowPath = this.pagePath.startsWith('/') ? this.pagePath.slice(1) : this.pagePath
          // 检查页面路径是否已经包含flow前缀
          if (flowPath.startsWith('flow/')) {
            baseUrl = `${this.basePath}#${flowPath}`
          } else {
            baseUrl = `${this.basePath}#flow/${flowPath}`
          }
        }
        return this.addQueryParamsToHashUrl(baseUrl, queryString)
      }
      // 默认使用基础路径
      console.info('使用默认路径:', this.basePath)
      return this.addQueryParamsToHashUrl(this.basePath, queryString)
    }
  },
  methods: {

    // 在页面加载前读取所有参数
    readParams() {
      removeCookie('system_type')
      setCookie('system_type', '')
      setCookie('token', '')
      // 读取其他可能的查询参数
      const otherParams = Object.keys(this.$route.query)
        .filter(key => key !== 'customPath')
        .reduce((acc, key) => {
          acc[key] = this.$route.query[key]
          return acc
        }, {})

      if (Object.keys(otherParams).length > 0) {
        if (otherParams.system_type) {
          setCookie('token', localStorage.getItem('master_token'))
          setCookie('system_type', otherParams.system_type)
        }
      }
    },

    handleIframeLoad() {
      // 本地iframe通信无需跨域，可直接操作（可选）
      const iframeWindow = this.$refs.localIframe.contentWindow
      // 打印iframe的location信息
      // 比如调用外部项目的全局方法
      if (iframeWindow.externalMethod) {
        iframeWindow.externalMethod()
      }
    },

    // 获取需要传递的查询参数（除了customPath）
    getQueryParams() {
      return Object.keys(this.$route.query)
        .filter(key => key !== 'customPath')
        .reduce((acc, key) => {
          acc[key] = this.$route.query[key]
          return acc
        }, {})
    },

    // 构建查询参数字符串
    buildQueryString(params) {
      if (!params || Object.keys(params).length === 0) {
        return ''
      }
      return '?' + Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&')
    },

    // 将查询参数添加到URL中（添加到hash内部）
    addQueryParamsToHashUrl(url, queryString) {
      if (!queryString || queryString === '?') {
        return url
      }
      // 检查URL是否包含hash
      const hashIndex = url.indexOf('#')

      if (hashIndex === -1) {
        // URL没有hash，添加#/作为基础hash，然后添加参数
        return `${url}#/${queryString.slice(1)}`
      } else {
        // URL有hash
        const baseUrl = url.slice(0, hashIndex)
        const hashPart = url.slice(hashIndex)
        // 检查hash内部是否已经包含查询参数
        const hashHasQuery = hashPart.includes('?')

        if (hashHasQuery) {
          // hash内部已有查询参数，追加参数
          return `${baseUrl}${hashPart}&${queryString.slice(1)}`
        } else {
          // hash内部没有查询参数，直接添加参数
          return `${baseUrl}${hashPart}${queryString}`
        }
      }
    },

    // 保留原方法作为兼容
    addQueryParamsToUrl(url, queryString) {
      if (!queryString) {
        return url
      }

      // 检查URL是否已经包含查询参数
      const hasQuery = url.includes('?')
      // 检查URL是否包含hash
      const hashIndex = url.indexOf('#')

      if (hashIndex === -1) {
        // URL没有hash，直接添加参数
        return hasQuery ? `${url}&${queryString.slice(1)}` : `${url}${queryString}`
      } else {
        // URL有hash，需要在hash前添加参数
        const baseUrl = url.slice(0, hashIndex)
        const hashPart = url.slice(hashIndex)
        return hasQuery ? `${baseUrl}&${queryString.slice(1)}${hashPart}` : `${baseUrl}${queryString}${hashPart}`
      }
    }
  },
  beforeDestroy() {
    // 销毁时清空iframe
    const iframe = this.$refs.localIframe
    if (iframe) {
      iframe.src = 'about:blank'
    }
  }
}
</script>

<style scoped>
.iframe-container {
  position: relative;
  width: calc(100vw - 140px);
  height: calc(100vh - 105px);
  overflow: hidden;
}

.iframe-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// src/components/Admin/Flow/FlowIframe.vue